import { ActivationDetailPage, ActivationListPage } from '@features/activation-tracking';
import { AuditPage } from '@features/audit';
import { BulkMigrationPage } from '@features/bulk-migrations';
import { CatalogComponentsTab, CatalogLayout, CatalogProductsTab } from '@features/catalog';
import {
    ClientBillingTab,
    ClientComponentsTab,
    ClientDetailLayout,
    ClientFlagsTab,
    ClientGeneralTab,
    ClientProductsTab,
    ClientsPage,
    NewClientComingSoon,
    ProvisionOwnerPage,
} from '@features/clients';
import { FlagsPage } from '@features/feature-flags';
import { OverviewPage } from '@features/overview';
import { OrgsPage } from '@features/tenants';
import { createBrowserRouter, Navigate } from 'react-router';

import { DashboardLayout } from './DashboardLayout';
import { FlagDetailRoute } from './routes/FlagDetailRoute';
import { LegacyFlagRedirect } from './routes/LegacyFlagRedirect';
import { NotFoundRoute } from './routes/NotFoundRoute';
import { ReleaseComponentRoute } from './routes/ReleaseComponentRoute';
import { RootLayout } from './routes/RootLayout';
import { SignInRoute } from './routes/SignInRoute';

/**
 * The route table — this file replaces the whole `app/` directory the Next App
 * Router used to derive routing from.
 *
 * The trade is deliberate: filesystem routing was implicit and free, this is
 * explicit and costs one entry per screen. In exchange the two things that were
 * *awkward* in the file tree are now plain — the auth gate is a nesting
 * relationship you can see, and `/flags/:key` is a runtime match rather than a
 * set of paths that must be known at build time.
 *
 * Route files stay thin, as they were before: `handle.title` carries what
 * `export const metadata` did, and screens live in `@features/*`.
 */

export const router = createBrowserRouter([
    {
        element: <RootLayout />,
        children: [
            { index: true, element: <Navigate to="/overview" replace /> },
            { path: 'sign-in', element: <SignInRoute />, handle: { title: 'Sign in' } },
            {
                // Every child of this layout is gated. Adding a screen here is
                // all it takes to protect it.
                element: <DashboardLayout />,
                children: [
                    { path: 'overview', element: <OverviewPage />, handle: { title: 'Overview' } },
                    {
                        path: 'catalog',
                        element: <CatalogLayout />,
                        handle: { title: 'Catalog' },
                        children: [
                            // No index tab: each section owns a path, so a link
                            // opens the section it names. `/catalog` is left
                            // with nothing to mark, hence the redirect.
                            { index: true, element: <Navigate to="/catalog/flags" replace /> },
                            {
                                path: 'flags',
                                handle: { title: 'Flags' },
                                children: [
                                    { index: true, element: <FlagsPage /> },
                                    { path: ':key', element: <FlagDetailRoute /> },
                                ],
                            },
                            {
                                path: 'components',
                                handle: { title: 'Components' },
                                children: [
                                    { index: true, element: <CatalogComponentsTab /> },
                                    // Nested so the Components tab stays marked.
                                    // `release` is a fixed segment, so it cannot
                                    // collide with a commercial component path.
                                    {
                                        path: 'release/:id',
                                        element: <ReleaseComponentRoute />,
                                        handle: { title: 'Release component' },
                                    },
                                ],
                            },
                            {
                                path: 'products',
                                element: <CatalogProductsTab />,
                                handle: { title: 'Products' },
                            },
                        ],
                    },
                    // Where the catalog used to live. Kept because a URL this
                    // product has served is a promise someone may have saved,
                    // and a redirect costs a line where a 404 costs a support
                    // message.
                    {
                        path: 'flags',
                        children: [
                            { index: true, element: <Navigate to="/catalog/flags" replace /> },
                            { path: ':key', element: <LegacyFlagRedirect /> },
                        ],
                    },
                    { path: 'addons', element: <Navigate to="/catalog/products" replace /> },
                    {
                        path: 'clients',
                        handle: { title: 'Clients' },
                        children: [
                            { index: true, element: <ClientsPage /> },
                            {
                                path: 'new',
                                element: <NewClientComingSoon />,
                                handle: { title: 'New client' },
                            },
                            {
                                // The tabs nest so each is linkable and keeps
                                // the `?env=&slot=` the screen is scoped by;
                                // `/owner` stays a sibling because it is a task
                                // that ends, not a view of the client.
                                path: ':tenantId',
                                element: <ClientDetailLayout />,
                                children: [
                                    { index: true, element: <ClientGeneralTab /> },
                                    {
                                        path: 'billing',
                                        element: <ClientBillingTab />,
                                        handle: { title: 'Billing' },
                                    },
                                    // The same three the catalog lists, seen
                                    // from one museum instead of from the
                                    // platform.
                                    {
                                        path: 'flags',
                                        element: <ClientFlagsTab />,
                                        handle: { title: 'Feature Flags' },
                                    },
                                    {
                                        path: 'components',
                                        element: <ClientComponentsTab />,
                                        handle: { title: 'Components' },
                                    },
                                    {
                                        path: 'products',
                                        element: <ClientProductsTab />,
                                        handle: { title: 'Products' },
                                    },
                                ],
                            },
                            {
                                path: ':tenantId/owner',
                                element: <ProvisionOwnerPage />,
                                handle: { title: 'Add the owner' },
                            },
                        ],
                    },
                    {
                        // Read-only: how far each museum got in Feature
                        // activation. The detail keeps the list's `?env=&slot=`.
                        path: 'activation-tracking',
                        handle: { title: 'Feature Activation' },
                        children: [
                            { index: true, element: <ActivationListPage /> },
                            {
                                path: ':tenantId',
                                element: <ActivationDetailPage />,
                                handle: { title: 'Museum' },
                            },
                        ],
                    },
                    {
                        // Bulk Salesforce → V2 migration, one module at a time.
                        // Module, job and step live in `?module=&report=&view=`,
                        // beside the `?env=&slot=` target, so one path covers
                        // every step — a launch's final report (`view=final`)
                        // included, which is the link an operator shares.
                        path: 'migrations',
                        element: <BulkMigrationPage />,
                        handle: { title: 'Salesforce migration' },
                    },
                    { path: 'orgs', element: <OrgsPage />, handle: { title: 'Client Orgs' } },
                    { path: 'audit', element: <AuditPage />, handle: { title: 'Audit Log' } },
                    { path: '*', element: <NotFoundRoute />, handle: { title: 'Not found' } },
                ],
            },
        ],
    },
]);
